/**
 * OWA-1457: a charge whose outcome is unknown. The payments service sends a charge to the processor
 * once and never repeats it (OWA-1206), so a lost answer, a 5xx, a 408 or a 429 may mean the card
 * was charged. Such a charge is not a refusal, and charging again could charge the card twice.
 */
export class PaymentOutcomeUnknownError extends Error {
    // The HTTP status of the answer, when there was one (OWA-1454)
    readonly status?: number;

    constructor(message: string, status?: number) {
        super(message);
        this.name = 'PaymentOutcomeUnknownError';
        this.status = status;
    }
}

/**
 * A charge the Practice server refused before sending it to the processor (401 or 403). It is not a
 * refusal by the processor, so the transaction is not marked refused.
 */
export class PaymentNotSentError extends Error {
    // 401 or 403. For a portal charge only a 403 proves it was never sent: the payments service
    // answers a processor 401 after the send as a 401, and treats that charge as unknown (OWA-1454).
    readonly status?: number;

    constructor(message: string, status?: number) {
        super(message);
        this.name = 'PaymentNotSentError';
        this.status = status;
    }
}

/**
 * OWA-1454: the payments service refused a portal charge for good (PORTAL_CHARGE_REFUSED): the
 * processor declined it, or refused it outright. Retrying its transactionId always answers the
 * same, so another try is a new charge with a new transactionId.
 */
export class PortalChargeRefusedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'PortalChargeRefusedError';
    }
}

/**
 * OWA-1454: the payments service could not confirm a portal charge within its reconciliation
 * window (PORTAL_CHARGE_OPERATOR_REQUIRED). Its outcome is still unknown, the firm has to check it
 * with the processor, and the client must never replace it with a new charge.
 */
export class PortalChargeOperatorError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'PortalChargeOperatorError';
    }
}

/** Whether an HTTP answer to a charge leaves its outcome unknown (no answer, 408, 429 or 5xx). */
export function chargeOutcomeUnknown(status: number | null | undefined): boolean {
    return !status || status >= 500 || status === 408 || status === 429;
}

/** Whether an HTTP answer to a charge means it was never sent (401 or 403). */
export function chargeNotSent(status: number | null | undefined): boolean {
    return status === 401 || status === 403;
}

/**
 * What the Firm User reads when a charge's outcome is unknown. The technical detail goes to the
 * console only: it can hold a URL, and a 503's "try again later" contradicts this advice.
 */
export function chargeUnknownMessage(): string {
    return 'Outlaw could not confirm whether this charge went through, because the payment server did not answer. ' +
        'Check the transaction\'s status before you charge again, so the card is not charged twice.';
}
